import { useDeleteAnimalMedia, useInfiniteAnimalMedia, useUploadAnimalCover, useUploadAnimalMedia } from '@/api/hooks';
import { MediaLightbox, type MediaLightboxEntry } from '@/components/animals/MediaLightbox';
import { VideoPreviewThumb } from '@/components/animals/VideoPreviewThumb';
import { EmptyState } from '@/components/ui/EmptyState';
import { ImageCropModal } from '@/components/ui/ImageCropModal';
import { RowActionsDropdown, RowActionsMenuItem } from '@/components/ui/RowActionsDropdown';
import { Button } from '@/components/ui/Button';
import { VideoFrameCaptureModal } from '@/components/ui/VideoFrameCaptureModal';
import { cn } from '@/lib/cn';
import { getErrorMessage } from '@/lib/api';
import { mediaGridPreviewUrl } from '@/lib/mediaPreview';
import { useInView } from '@/lib/useInView';
import { loadRemoteImageForCrop, revokeCropObjectUrl } from '@/lib/cropImage';
import {
  extractVideoThumbnailFile,
  getCachedVideoFrameUrl,
  getOrExtractVideoPreviewUrl,
  revokeFrameObjectUrl,
} from '@/lib/videoFrame';
import type { AnimalMediaItem } from '@/types/api';
import { ImageOff, Star, Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

export function AnimalMediaGrid({
  animalId,
  canDelete = true,
}: {
  animalId: number;
  canDelete?: boolean;
}) {
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteAnimalMedia(animalId);
  const media = data?.pages.flatMap((page) => page.media) ?? [];
  const totalCount = data?.pages[0]?.meta?.total ?? media.length;
  const [loadMoreRef, loadMoreInView] = useInView('320px');
  const uploadMedia = useUploadAnimalMedia(animalId);
  const uploadCover = useUploadAnimalCover(animalId);
  const deleteMedia = useDeleteAnimalMedia(animalId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeId, setActiveId] = useState<number | null>(null);
  const active = activeId == null ? null : media.find((item) => item.id === activeId) ?? null;
  const [activePoster, setActivePoster] = useState<string | undefined>(undefined);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [frameVideoSrc, setFrameVideoSrc] = useState<string | null>(null);
  const [frameOpen, setFrameOpen] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!loadMoreInView || !hasNextPage || isFetchingNextPage) return;
    void fetchNextPage();
  }, [loadMoreInView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (!active || active.media.media_kind !== 'video') {
      setActivePoster(undefined);
      return;
    }

    if (active.media.thumbnail_url) {
      setActivePoster(active.media.thumbnail_url);
      return;
    }

    const cached = getCachedVideoFrameUrl(active.media.url);
    if (cached) {
      setActivePoster(cached);
      return;
    }

    let cancelled = false;
    void getOrExtractVideoPreviewUrl(active.media.url)
      .then((url) => {
        if (!cancelled) setActivePoster(url);
      })
      .catch(() => {
        if (!cancelled) setActivePoster(undefined);
      });

    return () => {
      cancelled = true;
    };
  }, [active]);

  async function handleSetCover(file: File) {
    setError('');
    try {
      await uploadCover.mutateAsync({ file });
      setCropOpen(false);
      revokeCropObjectUrl(cropSrc);
      setCropSrc(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao definir foto de capa.'));
      throw err;
    }
  }

  async function handleDeleteItem(item: MediaLightboxEntry) {
    const label = item.media.media_kind === 'video' ? 'este vídeo' : 'esta foto';
    if (!window.confirm(`Remover ${label} da galeria do animal?`)) return;

    setError('');
    try {
      await deleteMedia.mutateAsync(item.id);
      if (activeId === item.id) setActiveId(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao remover a mídia.'));
    }
  }

  async function openCoverFromPhoto(item: MediaLightboxEntry) {
    setError('');
    try {
      const objectUrl = await loadRemoteImageForCrop(item.media.url);
      revokeCropObjectUrl(cropSrc);
      setCropSrc(objectUrl);
      setCropOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível preparar a foto para recorte.'));
    }
  }

  function openCoverFromVideo(item: MediaLightboxEntry) {
    setError('');
    setFrameVideoSrc(item.media.url);
    setFrameOpen(true);
  }

  async function handleFrameCaptured(objectUrl: string) {
    revokeCropObjectUrl(cropSrc);
    setCropSrc(objectUrl);
    setFrameOpen(false);
    setFrameVideoSrc(null);
    setCropOpen(true);
  }

  async function handleUploadFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    if (!isImage && !isVideo) {
      setError('Selecione uma foto ou vídeo.');
      return;
    }

    setError('');
    try {
      if (isVideo) {
        let thumbnail: File | undefined;
        try {
          thumbnail = await extractVideoThumbnailFile(file);
        } catch {
          // Preview sem thumb ainda funciona; capa pode ser capturada depois.
        }
        await uploadMedia.mutateAsync({ file, thumbnail });
      } else {
        await uploadMedia.mutateAsync(file);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao enviar a mídia.'));
    }
  }

  const busy = uploadCover.isPending || uploadMedia.isPending || deleteMedia.isPending;

  if (isLoading) {
    return (
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="aspect-square rounded-xl bg-manejai-sand/40 animate-pulse" />
        ))}
      </div>
    );
  }

  const uploadToolbar = (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <p className="text-sm text-manejai-bark/60">
        {totalCount ? `${totalCount} item(ns) na galeria` : 'Nenhuma mídia na galeria ainda'}
      </p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        loading={uploadMedia.isPending}
        disabled={busy}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="h-4 w-4" />
        Enviar foto ou vídeo
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        className="hidden"
        onChange={handleUploadFile}
      />
    </div>
  );

  if (!isLoading && media.length === 0) {
    return (
      <>
        {uploadToolbar}
        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
        <EmptyState
          icon={ImageOff}
          title="Nenhuma mídia ainda"
          description="Envie uma foto ou vídeo pelo botão acima, ou capture pelo app no campo."
        />
      </>
    );
  }

  return (
    <>
      {uploadToolbar}
      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
        {media.map((item) => (
          <MediaTile
            key={item.id}
            item={item}
            canDelete={canDelete}
            busy={busy}
            onView={() => setActiveId(item.id)}
            onSetCover={() =>
              item.media.media_kind === 'video' ? openCoverFromVideo(item) : openCoverFromPhoto(item)
            }
            onDelete={() => handleDeleteItem(item)}
          />
        ))}
      </div>

      {(hasNextPage || isFetchingNextPage) && (
        <div ref={loadMoreRef} className="mt-4 flex justify-center py-2">
          {isFetchingNextPage && (
            <div className="grid w-full grid-cols-3 gap-3 sm:grid-cols-4">
              {[0, 1, 2].map((i) => (
                <div key={i} className="aspect-square rounded-xl bg-manejai-sand/40 animate-pulse" />
              ))}
            </div>
          )}
        </div>
      )}

      <MediaLightbox
        open={activeId != null}
        onClose={() => setActiveId(null)}
        items={media}
        activeId={activeId}
        onActiveChange={setActiveId}
        videoPosterUrl={activePoster}
        actions={(item) => (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                if (item.media.media_kind === 'video') {
                  openCoverFromVideo(item);
                } else {
                  void openCoverFromPhoto(item);
                }
              }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 px-3 py-1.5 text-sm text-white transition hover:bg-white/10 disabled:opacity-50"
            >
              <Star className="h-4 w-4" />
              {item.media.media_kind === 'video' ? 'Capturar capa do vídeo' : 'Usar como capa'}
            </button>
            {canDelete && (
              <button
                type="button"
                disabled={busy}
                onClick={() => handleDeleteItem(item)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-400/40 px-3 py-1.5 text-sm text-red-200 transition hover:bg-red-500/20 disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
                {item.media.media_kind === 'video' ? 'Remover vídeo' : 'Remover foto'}
              </button>
            )}
          </>
        )}
      />

      <VideoFrameCaptureModal
        open={frameOpen}
        videoSrc={frameVideoSrc}
        onClose={() => {
          if (busy) return;
          setFrameOpen(false);
          setFrameVideoSrc(null);
        }}
        onCapture={async (objectUrl) => {
          handleFrameCaptured(objectUrl);
        }}
        loading={uploadCover.isPending}
      />

      <ImageCropModal
        open={cropOpen}
        imageSrc={cropSrc}
        title="Foto de capa"
        description="Mova e redimensione o quadro sobre o frame capturado."
        confirmLabel="Definir como capa"
        aspect={1}
        onClose={() => {
          if (busy) return;
          setCropOpen(false);
          revokeCropObjectUrl(cropSrc);
          revokeFrameObjectUrl(cropSrc);
          setCropSrc(null);
        }}
        onConfirm={handleSetCover}
        loading={uploadCover.isPending}
      />
    </>
  );
}

function MediaTile({
  item,
  canDelete,
  busy,
  onView,
  onSetCover,
  onDelete,
}: {
  item: AnimalMediaItem;
  canDelete: boolean;
  busy: boolean;
  onView: () => void;
  onSetCover: () => void;
  onDelete: () => void;
}) {
  const isPhoto = item.media.media_kind === 'photo';

  return (
    <div className="group relative aspect-square overflow-hidden rounded-xl bg-manejai-ink/5">
      <button
        type="button"
        onClick={onView}
        className="block h-full w-full"
        aria-label={isPhoto ? 'Ver foto' : 'Ver vídeo'}
      >
        {isPhoto ? (
          <img
            src={mediaGridPreviewUrl(item.media)}
            alt="Captura do animal"
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition group-hover:scale-105"
          />
        ) : (
          <VideoPreviewThumb
            videoUrl={item.media.url}
            thumbnailUrl={item.media.thumbnail_url}
            alt="Miniatura do vídeo"
            lazy
          />
        )}
      </button>

      {item.is_primary && (
        <span className="pointer-events-none absolute left-1.5 top-1.5 z-[1] rounded-md bg-manejai-forest/90 px-1.5 py-0.5 text-[10px] font-medium text-white">
          Capa
        </span>
      )}

      <div
        className={cn(
          'absolute right-1 top-1 z-10',
          'opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100 sm:focus-within:opacity-100',
        )}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div
          className={cn(
            '[&_button]:h-7 [&_button]:w-7 [&_button]:rounded-md',
            '[&_button]:bg-black/55 [&_button]:text-white',
            '[&_button]:hover:bg-black/75 [&_button]:hover:text-white',
          )}
        >
          <MediaTileActionsMenu
            item={item}
            canDelete={canDelete}
            busy={busy}
            onSetCover={onSetCover}
            onDelete={onDelete}
          />
        </div>
      </div>
    </div>
  );
}

function MediaTileActionsMenu({
  item,
  canDelete,
  busy,
  onSetCover,
  onDelete,
}: {
  item: AnimalMediaItem;
  canDelete: boolean;
  busy: boolean;
  onSetCover: () => void;
  onDelete: () => void;
}) {
  const isPhoto = item.media.media_kind === 'photo';

  return (
    <RowActionsDropdown label="Ações da mídia">
      <RowActionsMenuItem
        icon={Star}
        label={isPhoto ? (item.is_primary ? 'Ajustar capa' : 'Usar como capa') : 'Capturar capa do vídeo'}
        onClick={() => {
          if (!busy) onSetCover();
        }}
      />
      {canDelete && (
        <RowActionsMenuItem
          icon={Trash2}
          label={isPhoto ? 'Remover foto' : 'Remover vídeo'}
          onClick={() => {
            if (!busy) onDelete();
          }}
          danger
        />
      )}
    </RowActionsDropdown>
  );
}

type TabId = 'timeline' | 'media';

export function HistoryTabs({
  tab,
  onChange,
}: {
  tab: TabId;
  onChange: (tab: TabId) => void;
}) {
  const tabs: { id: TabId; label: string }[] = [
    { id: 'timeline', label: 'Linha do tempo' },
    { id: 'media', label: 'Mídias' },
  ];

  return (
    <div className="flex gap-2 border-b border-manejai-sand/60 -mb-px">
      {tabs.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onChange(item.id)}
          className={cn(
            'px-3 py-2 text-sm font-medium border-b-2 transition-colors -mb-px',
            tab === item.id
              ? 'border-manejai-forest text-manejai-forest'
              : 'border-transparent text-manejai-bark/60 hover:text-manejai-bark',
          )}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}
